import React, { useCallback, useRef } from "react";
import useScrollProgress from "../../hooks/useScrollProgress";

/**
 * A statement that brightens word by word as it crosses the viewport, so
 * reading it and scrolling it are the same gesture.
 *
 * The arithmetic is in CSS (see .mw-lit-word): every frame this writes one
 * custom property on the container, and each word works out its own opacity
 * from its index. A forty-word paragraph therefore costs a single style write
 * per frame rather than forty React renders — driving this from state is what
 * makes the effect famous for janking.
 *
 * Reduced motion pins every word lit; the rule lives in the stylesheet.
 */

function LitText({
  text,
  as: Tag = "p",
  className = "",
  /* Progress 0 with the block's top near the bottom of the viewport, 1 once
     its base has climbed past the middle — so it finishes while it is still
     comfortably in view rather than as it leaves. */
  from = 0.86,
  to = 0.52
}) {
  const ref = useRef(null);
  const words = text.split(/\s+/);

  const onProgress = useCallback((progress) => {
    const node = ref.current;
    if (node) node.style.setProperty("--mw-lit", progress.toFixed(4));
  }, []);

  useScrollProgress({ ref, onProgress, from, to });

  return (
    <Tag ref={ref} className={className} style={{ "--mw-n": words.length }}>
      {words.map((word, i) => (
        <React.Fragment key={i}>
          <span className="mw-lit-word" style={{ "--mw-i": i }}>
            {word}
          </span>
          {i < words.length - 1 ? " " : null}
        </React.Fragment>
      ))}
    </Tag>
  );
}

export default LitText;
